import { useTranslation } from '@/hooks/useTranslation';
import { Button } from 'primereact/button';
import React from 'react';
import { authApi } from '@/services/authApi';
import { useLocation } from 'react-router-dom';

interface LoadingStateProps {
  onRetry?: () => void;
}

interface ErrorStateProps extends LoadingStateProps {
  error?: string;
}

export const LoadingState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <main className='min-h-screen bg-gray-50 flex items-center justify-center'>
      <div className='text-center p-8'>
        <i
          className='pi pi-spin pi-spinner text-4xl text-blue-500 mb-4'
          aria-hidden='true'
        ></i>
        <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
          {t('survey.loading')}
        </h2>
        <p className='text-gray-600'>{t('survey.loadingDescription')}</p>
      </div>
    </main>
  );
};

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Check if this is an authentication error
  const isAuthError =
    error?.toLowerCase().includes('authentication') ||
    error?.toLowerCase().includes('unauthorized');

  const handleLogin = () => {
    const returnUrl = location.pathname + location.search;
    authApi.login(returnUrl);
  };

  return (
    <main className='min-h-screen bg-gray-50 flex items-center justify-center'>
      <div className='text-center p-8 max-w-md'>
        <i
          className={`pi text-4xl mb-4 ${
            isAuthError
              ? 'pi-lock text-blue-500'
              : 'pi-exclamation-triangle text-red-500'
          }`}
          aria-hidden='true'
        ></i>
        <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
          {isAuthError ? t('survey.authRequired') : t('survey.error')}
        </h2>
        <p className='text-gray-600 mb-6'>
          {isAuthError ? t('survey.authRequiredDescription') : error}
        </p>
        {isAuthError ? (
          <Button
            label={t('survey.login')}
            icon='pi pi-sign-in'
            onClick={handleLogin}
            aria-label={t('survey.loginAriaLabel')}
            className='px-6 py-3'
          />
        ) : (
          onRetry && (
            <Button
              label={t('survey.tryAgain')}
              icon='pi pi-refresh'
              onClick={onRetry}
              aria-label={t('survey.retryAriaLabel')}
              className='px-6 py-3'
            />
          )
        )}
      </div>
    </main>
  );
};

export const EmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <main className='min-h-screen bg-gray-50 flex items-center justify-center'>
      <div className='text-center p-8 max-w-md'>
        <i
          className='pi pi-file text-4xl text-gray-400 mb-4'
          aria-hidden='true'
        ></i>
        <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
          {t('survey.noSurvey')}
        </h2>
        <p className='text-gray-600'>{t('survey.noSurveyDescription')}</p>
      </div>
    </main>
  );
};

export const LoadingInsightsState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <main className='min-h-screen bg-gray-50 flex items-center justify-center'>
      <div className='text-center p-8'>
        <i
          className='pi pi-spin pi-spinner text-4xl text-blue-500 mb-4'
          aria-hidden='true'
        ></i>
        <h3 className='text-2xl font-semibold text-gray-900 mb-2'>
          {t('survey.loadingInsights')}
        </h3>
        <p className='text-gray-600'>
          {t('survey.loadingInsightsDescription')}
        </p>
      </div>
    </main>
  );
};

export const CompletionState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <main className='max-w-4xl mx-auto p-6'>
      <div className='bg-white rounded-lg p-8 text-center shadow-sm border border-gray-200'>
        <div className='mb-4'>
          <svg
            className='w-16 h-16 mx-auto text-green-500'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
          {t('survey.completed')}
        </h2>
        <p className='text-gray-600'>{t('survey.completionMessage')}</p>
      </div>
    </main>
  );
};

export const AlreadyCompletedState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <main className='max-w-4xl mx-auto p-6'>
      <div className='bg-white rounded-lg p-8 text-center shadow-sm border border-gray-200'>
        <div className='mb-4'>
          <svg
            className='w-16 h-16 mx-auto text-green-500'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
          {t('survey.alreadyCompleted')}
        </h2>
        <p className='text-gray-600 whitespace-pre-line'>
          {t('survey.alreadyCompletedDescription')}
        </p>
      </div>
    </main>
  );
};
